import type { SteelOrderCsvImportFieldsFragment } from '@mbfab/client-api'
import { isPresent } from '@mbfab/shared'
import dayjs from 'dayjs'
import { useState } from 'react'

import type { DataTableColumn, DataTableExpandable } from '@/components/ui/data-table/data-table'
import { DataTable } from '@/components/ui/data-table/data-table'
import { OverflowText } from '@/components/ui/data-table/overflow-text'
import { valueOrDash } from '@/components/ui/data-table/value-or-dash'
import { StatusDot } from '@/components/ui/status-dot'

type ErrorRow = SteelOrderCsvImportFieldsFragment['summary']['errorRows'][number]

const ERROR_ROW_COLUMNS: Array<DataTableColumn<ErrorRow>> = [
  { key: 'rowNumber', title: '行番号', numeric: true, render: (errorRow) => errorRow.rowNumber },
  {
    key: 'message',
    title: 'エラー内容',
    render: (errorRow) => <OverflowText text={errorRow.message} mode="wrap" className="text-destructive" />,
  },
]

// エラー行の内側テーブル
function ErrorRowsTable({ errorRows }: { errorRows: SteelOrderCsvImportFieldsFragment['summary']['errorRows'] }) {
  return (
    <DataTable
      columns={ERROR_ROW_COLUMNS}
      rows={errorRows}
      rowKey={(errorRow) => String(errorRow.rowNumber)}
      testIdPrefix="steel-order-csv-import-error-rows"
    />
  )
}

// 発注照合の「取込履歴」タブ。列・値は v1 SteelOrderCsvImportsTable を正とする
// (kind/source/summary.*/createdByName)。実行ユーザーは手動取込のみ値がある（S3自動連携はダッシュ）
export function ImportHistoryTable({
  csvImports,
  loading,
  pagination,
  onChangePage,
}: {
  csvImports: SteelOrderCsvImportFieldsFragment[]
  loading: boolean
  pagination: { page: number; pageSize: number; totalCount: number }
  onChangePage: (page: number) => void
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const toggleExpanded = (id: string) => {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  // 一番左はその取込を表すファイル名（docs/develop/client_ui/components/data-table.md「列の表示」）
  const columns: Array<DataTableColumn<SteelOrderCsvImportFieldsFragment>> = [
    {
      key: 'fileName',
      title: 'ファイル名',
      keyColumn: true,
      minWidthClass: 'min-w-64',
      render: (record) => (
        <span data-testid={`steel-order-csv-import-row-${record.id}`}>
          <OverflowText text={record.fileName} />
        </span>
      ),
    },
    {
      key: 'createdAt',
      title: '取込日時',
      render: (record) => {
        // DateTime は UTC で返るため JST を明示して表示する（v1 と同じ）
        const createdAt = dayjs(record.createdAt).tz('Asia/Tokyo')
        return (
          <span className="text-foreground">
            {createdAt.format('YYYY/MM/DD')} <span className="text-muted-foreground">{createdAt.format('HH:mm')}</span>
          </span>
        )
      },
    },
    {
      key: 'kind',
      title: 'ファイル種別',
      render: (record) => (
        <span className="text-muted-foreground">
          {record.kind === 'deliveryTable' ? 'デリバリー管理表' : 'SCRMデータ'}
        </span>
      ),
    },
    {
      key: 'status',
      title: '結果',
      render: (record) => {
        if (record.status === 'succeeded') {
          return <StatusDot tone="ok" label="成功" />
        }
        if (record.status === 'partiallyFailed') {
          return <StatusDot tone="progress" label="一部エラー" />
        }
        return <StatusDot tone="ng" label="失敗" />
      },
    },
    {
      key: 'importedRowCount',
      title: '取込行数',
      numeric: true,
      render: (record) => (
        <>
          {record.summary.importedRowCount.toLocaleString()}
          <span className="text-muted-foreground"> / {record.summary.totalRowCount.toLocaleString()}</span>
        </>
      ),
    },
    {
      key: 'errorRowCount',
      title: 'エラー行数',
      numeric: true,
      render: (record) => {
        const errorRowCount = record.summary.errorRows.length
        return (
          <span className={errorRowCount > 0 ? 'font-medium text-destructive' : 'text-muted-foreground'}>
            {errorRowCount.toLocaleString()}
          </span>
        )
      },
    },
    {
      key: 'source',
      title: '取込経路',
      render: (record) => (
        <span className="text-muted-foreground">{record.source === 'manual' ? '手動アップロード' : 'S3自動連携'}</span>
      ),
    },
    {
      key: 'createdByName',
      title: '実行ユーザー',
      render: (record) => <>{valueOrDash(isPresent(record.createdByName) ? record.createdByName : null)}</>,
    },
  ]

  const expandable: DataTableExpandable<SteelOrderCsvImportFieldsFragment> = {
    isExpanded: (record) => expanded.has(record.id),
    onToggle: (record) => {
      toggleExpanded(record.id)
    },
    // エラーが無い取込は展開しても何も出せないため、エラー行がある取込だけ開けるようにする（既存挙動を維持）
    rowExpandable: (record) => record.summary.errorRows.length > 0,
    expandedRowTitle: (record) => (
      <>
        {record.fileName} のエラー行
        <span className="ml-2 text-xs font-medium text-muted-foreground">{record.summary.errorRows.length}件</span>
      </>
    ),
    expandedRowRender: (record) => <ErrorRowsTable errorRows={record.summary.errorRows} />,
    rowLabel: (record) => record.fileName,
  }

  return (
    <section>
      <DataTable
        columns={columns}
        rows={csvImports}
        rowKey={(record) => record.id}
        testIdPrefix="steel-order-csv-import"
        loading={loading}
        pagination={{ ...pagination, onChangePage }}
        emptyMessage="取込履歴はまだありません"
        expandable={expandable}
        toolbar={
          <div>
            <h2 className="text-base font-bold text-foreground">取込履歴</h2>
            {/* 手動アップロードとS3自動連携の取込が同じ一覧に混在することを明示する。
                「夜間の自動取込が通ったか」を見に来る利用者が、ここが監査面だと分かるようにする
                （docs/develop/client_ui/components/file-upload.md「実行と履歴を分ける」） */}
            <p className="mt-1 text-sm text-muted-foreground">
              画面からの手動アップロードと、SCRMのS3自動連携の両方の取込が並びます。
            </p>
          </div>
        }
      />
    </section>
  )
}
